import {
  For,
  Show,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";

import { getGetGameList } from "@/api/clientApi/home";
import { ExpandAnimationWrapper } from "@/components/Base/AnimationWrapper";
import { Input } from "@/components/Base/Input/Input";
import Popup from "@/components/Base/Popup/Popup";
import Icon from "@/components/Icon";
import { GameTag } from "@/enum/game";
import { globalSearch, useGlobalSearch } from "@/hooks/useGlobalSearch";
import { useI18n } from "@/i18n";
import { isMobile } from "@/lib/isMobile";
import {
  removeLocalStorageItem,
  setLocalStorageItem,
} from "@/lib/localStorage";
import { sysConfigStore } from "@/stores/sysConfigStore";

import {
  MainCategoryTypes,
  casinoSubCategoryList,
  sportsSubCategoryListBetby,
  sportsSubCategoryListFirst,
} from "./constants";

const RECOMMEND_BADGE_COUNT = 30;

type BadgeProps = {
  label: string;
  value: string;
  onClick?: () => void;
  onClose?: () => void;
};

const Badge = (props: BadgeProps) => {
  const isMobileClick = () => {
    props.onClick?.();
    props.onClose?.();
  };

  return (
    <div
      class="bg-general-bg6 flex h-[30px] cursor-pointer items-center gap-[6px] truncate px-[10px]"
      classList={{
        "py-[7.5px] rounded-[8px]": isMobile(),
        "py-[6.5px] rounded-[8px]": !isMobile(),
      }}
    >
      <div
        class="text-general-bright flex items-center"
        classList={{
          "text-[12px] leading-[15px] font-[500]": isMobile(),
          "text-[14px] leading-[17px] font-[400]": !isMobile(),
        }}
        onClick={isMobile() ? isMobileClick : props.onClick}
      >
        {props.label}
      </div>
      <Show when={props.onClose} fallback={<></>}>
        <Icon
          name="general/xx2"
          class="text-general-icon size-[9px]"
          onClick={props.onClose}
        />
      </Show>
    </div>
  );
};

type SearchInputContainerProps = {
  debouncedFetch: (isLoadMore?: boolean) => void;
  gameItemPreviewScrollToTop: () => void;
};

export const SearchInputContainer = (props: SearchInputContainerProps) => {
  let inputRef: HTMLInputElement | undefined;
  let popupRef: HTMLDivElement | undefined;

  const { t } = useI18n();

  const mainCategoryList = createMemo(() => {
    return sysConfigStore.sport_config.sport_priority_type === 0
      ? [{ key: "Casino", value: "Casino" }]
      : [
          { key: "Casino", value: "Casino" },
          { key: "Sports", value: "Sports" },
        ];
  });

  const {
    setCategoryIsOpen,
    setCurMainCategory,
    setPopupVisible,
    setSearchCasinoParams,
    setSearchHistory,
    setSearchRecommend,
    setSearchSportsParams,
    setSearchValue,
  } = useGlobalSearch();

  const sportsSubCategoryList = createMemo(() => {
    const sportType = sysConfigStore.sport_config.sport_priority_type;
    if (sportType === 1) return sportsSubCategoryListBetby;
    if (sportType === 2) return sportsSubCategoryListFirst;
    return [];
  });

  // 获取当前分类的显示值
  const getCurrentCategoryDisplay = createMemo(() => {
    const currentItem = mainCategoryList().find(
      (item) => item.key === globalSearch().curMainCategory,
    );
    return currentItem?.value || globalSearch().curMainCategory;
  });

  // 主分类弹窗开关切换
  const handleMainCategoryToggle = () => {
    setCategoryIsOpen(!globalSearch().categoryIsOpen);
  };

  // 主分类选择
  const handleMainCategorySelect = (key: MainCategoryTypes) => () => {
    setCurMainCategory(key);
    const firstSub =
      key === "Casino"
        ? casinoSubCategoryList?.[0]?.value
        : sportsSubCategoryList()?.[0]?.value;

    if (key === "Casino") {
      setSearchCasinoParams({
        gclass: firstSub,
        page: 1,
      });
    }

    if (key === "Sports") {
      setSearchSportsParams({
        from_type: sysConfigStore.sport_config.sport_priority_type,
        sport_id: firstSub as number,
        page: 1,
      });
    }

    setCategoryIsOpen(false);

    // 切换分类后重新搜索并滚动到顶部
    queueMicrotask(() => {
      props.debouncedFetch();
      // 确保每次切换主标签都滚动到顶部
      props.gameItemPreviewScrollToTop();
    });
  };

  // 搜寻标签点击
  const handleBadgeClick = (item: string, isCloseAfterClick = true) => {
    setSearchValue(item);

    if (globalSearch().curMainCategory === "Casino") {
      setSearchCasinoParams({
        name: item,
        page: 1,
      });
    }
    if (globalSearch().curMainCategory === "Sports") {
      setSearchSportsParams({
        query: item,
        page: 1,
      });
    }

    queueMicrotask(() => props.debouncedFetch(false));

    if (isCloseAfterClick) return;
    setPopupVisible(false);
  };

  // 点击外部关闭弹窗
  onMount(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!inputRef?.contains(target) && !popupRef?.contains(target)) {
        setPopupVisible(false);
      }
    };
    // 用 mousedown 更早触发，避免被 blur 抢先触发
    document.addEventListener("mousedown", handleClickOutside);
    onCleanup(() => {
      document.removeEventListener("mousedown", handleClickOutside);
    });
  });

  // ======= LocalStorage =======
  const removeSearchItem = (item: string) => {
    const newList = globalSearch().searchHistory.filter(
      (i: string) => i !== item,
    );
    setSearchHistory(newList);
    setLocalStorageItem("global-search", newList);
  };

  const clearSearchHistory = () => {
    setSearchHistory([]);
    removeLocalStorageItem("global-search");
  };
  // ======= LocalStorage =======

  // 推荐标签由 HOT GAME 取值
  createEffect(() => {
    (async () => {
      const res = await getGetGameList({
        page: "1",
        page_size: RECOMMEND_BADGE_COUNT.toString(),
        name: "",
        sort: "0",
        maker_tag: "",
        tag: GameTag.hot,
      });

      const gameNameList = res.list?.map((item) => item.name) as string[];
      setSearchRecommend(gameNameList);
    })();
  });

  onCleanup(() => {
    setCategoryIsOpen(false);
    setSearchValue("");
    setPopupVisible(false);
  });

  //  ======= SearchInputPrefixRender =======
  const SearchInputPrefixRender = () => (
    <div class="flex items-center gap-[4px]">
      <Icon name="search" class="text-general-icon size-[15px]" />
    </div>
  );
  const setParams = (val: string) => {
    if (globalSearch().curMainCategory === "Casino") {
      setSearchCasinoParams({
        name: val.length >= 3 ? val : "",
        page: 1,
      });
    }

    if (globalSearch().curMainCategory === "Sports") {
      setSearchSportsParams({
        query: val.length >= 3 ? val : "",
        page: 1,
      });
    }
  };

  const [isFocused, setIsFocused] = createSignal(false);

  return (
    <div class="relative flex gap-[5px]">
      <div
        class="text-general-light bg-general-searchbg flex cursor-pointer select-none items-center justify-between rounded-[12px] p-[10px] font-[500]"
        classList={{
          "text-[12px] min-w-[92px] leading-[100%]": isMobile(),
          "text-[15px] min-w-[110px] leading-[18px]": !isMobile(),
        }}
        onClick={handleMainCategoryToggle}
      >
        {t(`home.${getCurrentCategoryDisplay().toLowerCase()}`)}
        <Icon
          name="general/arrow_left"
          class={`text-general-icon size-[13px] transition-transform duration-200 ${
            globalSearch().categoryIsOpen ? "rotate-90" : "rotate-270"
          } `}
        />
      </div>
      <Input
        ref={(el: HTMLInputElement | undefined) => (inputRef = el)}
        disableFloatLabel
        containerClass="flex-1"
        placeholder={`${t("search.search_game_name")} | ${t("home.providers")}`}
        class="bg-general-searchbg relative z-[25] h-[40px] w-full flex-1 !pl-[10px]"
        prefix={<SearchInputPrefixRender />}
        noDefaultBorder
        showClose={!!globalSearch().searchValue && isFocused()}
        onClose={() => {
          inputRef?.focus();
          setSearchValue("");
          setParams("");
          props.debouncedFetch();
        }}
        onFocus={() => {
          setIsFocused(true);
          if (!globalSearch().searchValue.trim()) {
            setPopupVisible(true);
          }
          if (globalSearch().searchValue.length >= 3) {
            setPopupVisible(true);
          }

          const globalSearchElement = document.getElementById("global-search");
          if (globalSearchElement) {
            globalSearchElement.style.overflow = "hidden";
          }
        }}
        value={globalSearch().searchValue}
        onInput={(e) => {
          if (!isFocused()) setIsFocused(true);
          const val = e.currentTarget.value.trim();
          setSearchValue(val);

          if (val.length === 0) setPopupVisible(true);
          // 如果输入大於3個字符，则关闭弹窗
          if (val.length >= 3) setPopupVisible(false);

          setParams(val);

          if (val.length >= 3 || val.length === 0) props.debouncedFetch(false);
        }}
        onKeyDown={async (e: KeyboardEvent) => {
          if (e.key === "Escape") {
            setPopupVisible(false);
            inputRef?.blur();
          }
        }}
        onBlur={() => {
          if (isMobile()) {
            requestAnimationFrame(() => {
              setIsFocused(false);
            });
          }
        }}
      />

      {/* 搜寻弹窗, 搜寻标签历史, 推荐游戏标签 */}
      <Show when={globalSearch().isPopupVisible} fallback={<></>}>
        {/* 遮罩层 z-[21] 比 GameItem 高 */}
        <div
          class="pointer-events-auto fixed left-0 top-0 z-[21] h-screen w-screen bg-transparent"
          onClick={() => setPopupVisible(false)}
        />

        <div
          ref={(el) => (popupRef = el)}
          class="border-general-sk bg-general-bg3 absolute left-0 top-[40px] z-[100] grid w-full overflow-hidden rounded-[6px] border shadow-lg"
          classList={{
            "mt-[6px]": isMobile(),
            "mt-[12px]": !isMobile(),
          }}
        >
          <div
            class="text-general-light flex flex-col gap-[12px] overflow-hidden"
            classList={{
              "p-[12px]": isMobile(),
              "px-[12px] py-[20px]": !isMobile(),
            }}
          >
            <div
              class="text-general-bright flex items-start justify-center"
              classList={{
                "text-[12px]": isMobile(),
                "text-[16px]": !isMobile(),
              }}
            >
              {t("search.search_min_char")}
            </div>
            {globalSearch().searchHistory.length > 0 && (
              <div class="flex flex-col gap-[12px]">
                <div class="flex h-[20px] items-center justify-between">
                  <span
                    class="font-[700] leading-[16px]"
                    classList={{
                      "text-[13px] ": isMobile(),
                      "text-[14px]": !isMobile(),
                    }}
                  >
                    {t("affiliate.history")}
                  </span>
                  <span
                    class="text-general-bright flex select-none gap-[10px] font-[500]"
                    classList={{
                      "text-[12px] leading-[15px]": isMobile(),
                      "text-[14px] leading-[17px]": !isMobile(),
                    }}
                    onClick={clearSearchHistory}
                  >
                    <Icon
                      name="general/delete"
                      class="text-general-icon size-[15px] cursor-pointer"
                    />
                  </span>
                </div>
                <div class="flex max-h-[70px] min-h-[30px] flex-wrap items-center gap-[10px] overflow-hidden">
                  <For each={globalSearch().searchHistory}>
                    {(item) => (
                      <Badge
                        label={item}
                        value={item}
                        onClick={() => handleBadgeClick(item, false)}
                        onClose={() => removeSearchItem(item)}
                      />
                    )}
                  </For>
                </div>
              </div>
            )}

            <div class="flex flex-col gap-[12px]">
              <div
                class="flex h-[20px] items-center font-[700] leading-[16px]"
                classList={{
                  "text-[13px]": isMobile(),
                  "text-[14px]": !isMobile(),
                }}
              >
                {t("search.recommend")}
              </div>
              <div class="flex max-h-[70px] min-h-[30px] flex-wrap items-center gap-[10px] overflow-hidden">
                <For each={globalSearch().searchRecommend}>
                  {(item) => (
                    <Badge
                      label={item}
                      value={item}
                      onClick={() => handleBadgeClick(item, false)}
                    />
                  )}
                </For>
              </div>
            </div>
          </div>
        </div>
      </Show>

      {/* 主分类选择(向下弹出)*/}
      <Show when={globalSearch().categoryIsOpen} fallback={<></>}>
        {/* 遮罩层 */}
        <div
          class="pointer-events-auto fixed left-0 top-0 z-[21] h-screen w-screen bg-transparent"
          onClick={() => setCategoryIsOpen(false)}
        />
        <div
          class="absolute left-0 top-[40px] z-[21]"
          classList={{
            "w-[232px]": !isMobile(),
            "w-[175px]": isMobile(),
          }}
        >
          <ExpandAnimationWrapper state={globalSearch().categoryIsOpen}>
            <div
              class="bg-general-bg3 text-general-light mt-[4px] flex flex-col overflow-hidden shadow-md"
              classList={{
                "rounded-[6px]": !isMobile(),
                "rounded-[8px]": isMobile(),
              }}
              style="box-shadow: 0 3px 10px 0 rgba(4, 4, 4, 0.30);"
            >
              <For each={mainCategoryList()}>
                {(item, index) => {
                  const isFirst = index() === 0;
                  const isLast = index() === mainCategoryList().length - 1;

                  return (
                    <div
                      class="flex cursor-pointer select-none items-center justify-between py-[9px] pl-[12px] pr-[17px] text-[14px] font-[500] leading-[100%]"
                      classList={{
                        "h-[42px]": !isMobile(),
                        "h-[52px]": isMobile(),
                        "bg-general-bg4":
                          globalSearch().curMainCategory === item.key,
                        "bg-general-bg3":
                          globalSearch().curMainCategory !== item.key,
                        "hover:bg-general-bg4": true,
                        "rounded-t-[6px]": isFirst,
                        "rounded-b-[6px]": isLast,
                      }}
                      onClick={handleMainCategorySelect(
                        item.key as MainCategoryTypes,
                      )}
                    >
                      {t(`home.${item.key.toLowerCase()}`)}
                      {globalSearch().curMainCategory === item.key && (
                        <span class="text-general-icon text-[14px]">
                          <Icon
                            name="correct"
                            class="text-general-main size-[17px]"
                          />
                        </span>
                      )}
                    </div>
                  );
                }}
              </For>
            </div>
          </ExpandAnimationWrapper>
        </div>
      </Show>

      {/* 主分类选择(底部往上弹出) - H5 */}
      {/* <Show when={isMobile()} fallback={<></>}>
        <Popup
          show={globalSearch().categoryIsOpen && isMobile()}
          title={t("common.select")}
          contentClass="max-h-[106px] pb-[10px] px-0 pt-0"
          onClose={() => setCategoryIsOpen(false)}
        >
          <For each={mainCategoryList()}>
            {(item) => {
              return (
                <div
                  class="clickable hover:bg-general-bg3 flex-between text-general-light h-[48px] px-[20px] py-[15px] text-[14px] font-[500] leading-[100%]"
                  classList={{
                    "bg-general-bg3":
                      globalSearch().curMainCategory === item.key,
                  }}
                  onClick={handleMainCategorySelect(
                    item.key as MainCategoryTypes,
                  )}
                >
                  {t(`home.${item.key.toLowerCase()}`)}
                  <Show
                    when={globalSearch().curMainCategory === item.key}
                    fallback={<></>}
                  >
                    <Icon
                      name="correct"
                      class="text-general-main size-[20px]"
                    />
                  </Show>
                </div>
              );
            }}
          </For>
        </Popup>
      </Show> */}
    </div>
  );
};
